import React from "react";
import { GridTable } from "../../Sidebar/SidebarStyles";
import { fetchAllLobs } from "../../ApiSevices/FileUploadApis";
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, TextField, Tooltip } from "@mui/material";
import { Box, Typography } from "@mui/material";
import { 
    addSemanticKey, addSemanticValue, deleteSemanticKey, deleteSemanticValue, 
    exportSemanticRecord, 
    getQuestionCodes, getSemanticKeyData, getSemanticValueData, getTestResult 
} from "../../ApiSevices/SemanticService";
import { Delete, Visibility } from "@mui/icons-material";
import { red } from "@mui/material/colors";
import SnackBarComponent from "../../Common/SnackBarComponent";

interface IProps {
    displaysidebar: boolean;
}

interface IState {
    loading: boolean,
    semanticKeyData: ISemanticKeyData[],
    semanticValueData: ISemanticValueData[],
    lobCodes: { Lob_Name: string; Lob_Code: string }[];
    questionCodes: { Id: number; ChekListQuestion: string, ShortQuestion: string }[];
    selectedLobCode: string | null;
    selectedQuestionCode: string | null;
    selectedSemanticKeyId: number | null;
    baseVariance: string | null;
    oppositeVariance: string | null;
    dialogState: IDialogState;
    deleteDialogState: IDeleteDialogState;
    semanticKeySearch: string | null;
    snackBar: {
        message: string;
        severity: 'error' | 'warning' | 'info' | 'success';
        open: boolean;
        onClose: () => void
    },
}

interface IDialogState {
    isOpen: boolean;
    dialogFor: 'baseVariance' | 'oppositeVariance' | null;
    semanticKeyId: number | null;
    baseVariance: string | null;
    oppositeVariance: string | null;
}

interface IDeleteDialogState {
    isOpen: boolean;
    dialogFor: 'baseVariance' | 'oppositeVariance' | null;
    recordId: number | null;
}

interface ISemanticKeyData {
    Id: number,
    KeyText: string,
    QuestionCode: string,
    LobCode: string,
    isActive: boolean
}

interface ISemanticValueData {
    Id: number,
    LookupKeyId: number,
    ValueText: string,
    isActive: boolean,
    MatchScore: any,
    BaseMatchScore?: any,
}

export class SemanticConfig extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            loading: false,
            semanticKeyData: [],
            semanticValueData: [],
            lobCodes: [],
            questionCodes: [],
            selectedLobCode: null,
            selectedQuestionCode: null,
            selectedSemanticKeyId: null,
            baseVariance: null,
            oppositeVariance: null,
            semanticKeySearch: null,
            dialogState: {
                isOpen: false,
                semanticKeyId: null,
                baseVariance: null,
                oppositeVariance: null,
                dialogFor: null
            },
            deleteDialogState: {
                isOpen: false,
                dialogFor: null,
                recordId: null
            },
            snackBar: {
                message: '',
                severity: 'info',
                open: false,
                onClose: () => {}
            }
        };
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async (): Promise<void> => {
        this.setState({ loading: true });
        try {
            const fetchResponse = await fetchAllLobs();
            if (fetchResponse && fetchResponse?.length > 0) {
                this.setState({ lobCodes: Array.from(new Set(fetchResponse)) });
            }
            const questionCodesResponse = await getQuestionCodes();
            if (questionCodesResponse && questionCodesResponse?.length > 0) {
                this.setState({ questionCodes: questionCodesResponse });
            }
        }
        catch (error) {
            console.error('Error in fetching the data:', error);
        }
        finally {
            this.setState({ loading: false });
            this.resetValues();
        }
    }

    getSemanticKeyData = async (): Promise<void> => {
        const { selectedLobCode, selectedQuestionCode } = this.state;
        if(!selectedLobCode || !selectedQuestionCode){
            return;
        }
        this.setState({ loading: true });
        try{
            const semanticKeyResponse = await getSemanticKeyData(selectedLobCode!, selectedQuestionCode!);
            this.setState({ semanticKeyData: semanticKeyResponse || [] });
        }catch (error) {
            console.error('Error in fetching the semantic key data:', error);
        }
        finally {
            this.setState({ loading: false });
            this.resetValues();
        }
    }

    exportData = async(): Promise<void> => {
        try{
            const { selectedLobCode, selectedQuestionCode } = this.state;
            this.setState({ loading: true });
            const response = await exportSemanticRecord(selectedLobCode || "", selectedQuestionCode || "");
    
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
    
            const url = window.URL.createObjectURL(blob);
    
            const link = document.createElement("a");
            link.href = url;
            link.download = `SemanticMaster_${selectedLobCode}_${selectedQuestionCode}.xlsx`;
    
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    
            window.URL.revokeObjectURL(url);
        }
        catch(error){
            console.error("Error in exporting data", error);
        }
        finally{
            this.setState({ loading: false });
        }
    }

    getSemanticValueData = async (semanticKeyId: number): Promise<void> => {
        this.setState({ loading: true });
        try{
            const semanticValueResponse = await getSemanticValueData(semanticKeyId);
            this.setState({ semanticValueData: semanticValueResponse?.map((data: ISemanticValueData) => ({ ...data, MatchScore: "-" })) || [] });
        }catch (error) {
            console.error('Error in fetching the semantic value data:', error);
        }
        finally {
            this.setState({ loading: false });
        }
    }

    getTestResult = async (): Promise<void> => {
        const { selectedSemanticKeyId, baseVariance, oppositeVariance } = this.state;
        this.setState({ loading: true });
        try{
            const testResultResponse = await getTestResult(selectedSemanticKeyId!, baseVariance!, oppositeVariance!);
            if (testResultResponse && testResultResponse?.length > 0) {
                const updatedSemanticValueData = this.state.semanticValueData.map((value) => {
                    const matchingResult = testResultResponse.find((result: any) => result.lookup_value_id === value.Id);
                    return matchingResult ? { ...value, MatchScore: matchingResult?.value_score, BaseMatchScore: matchingResult?.key_score } : value;
                });
                this.setState({ semanticValueData: updatedSemanticValueData });
            }
        }catch (error) {
            console.error('Error in fetching the test result:', error);
        }
        finally {
            this.setState({ loading: false });
        }
    }

    resetValues = () => {
        this.setState({
            selectedSemanticKeyId: null,
            baseVariance: null,
            oppositeVariance: null,
            semanticKeySearch: null,
            semanticValueData: []
        });
        this.setDialogState(false, null, null, null, null);
    }

    setDialogState = (isOpen: boolean, dialogFor: 'baseVariance' | 'oppositeVariance' | null, semanticKeyId: number | null, baseVariance: string | null, oppositeVariance: string | null) => {
        this.setState({
            dialogState: {
                isOpen: isOpen,
                dialogFor,
                semanticKeyId,
                baseVariance,
                oppositeVariance
            }
        });
    }

    setDeleteDialogState = (isOpen: boolean, dialogFor: 'baseVariance' | 'oppositeVariance' | null, recordId: number | null) => {
        this.setState({
            deleteDialogState: {
                isOpen: isOpen,
                dialogFor,
                recordId
            }
        });
    }

    getHeaderSection = (): JSX.Element => {
        return (
            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 10, fontSize: 11, marginRight: 10 }}>
                <Typography variant="h5" style={{ fontSize: 14 }}><b>Semantic Config</b></Typography>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button 
                        variant="contained" color="primary" size="small"
                        style={{ height: 30, fontSize: 11 }}
                        onClick={() => this.setDialogState(true, 'baseVariance', null, null, null)}
                    >
                        Add Base Variance Master
                    </Button>
                </div>
            </Box>
        );
    }

    getSemanticValueHeaderSection = (): JSX.Element => {
        const { selectedSemanticKeyId, baseVariance, oppositeVariance, semanticValueData } = this.state;
        return (
            <>
                <Box display="flex" flexDirection="row" gap={1} style={{width: '100%'}}>
                    <p><b>Opposite Variance Masters</b></p>
                        <Button 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        style={{ height: 20, fontSize: 11 }} 
                        disabled={!selectedSemanticKeyId || selectedSemanticKeyId <= 0}
                        onClick={() => this.setDialogState(true, 'oppositeVariance', selectedSemanticKeyId, null, null)}
                    >
                        Add Opposite Variance Master
                    </Button>
                </Box>
                <Box display="flex" gap={1} style={{ marginBottom: 10, minHeight: 0,marginTop: 10, padding: 10, border: '1px solid #ccc', borderRadius: 4 }}>
                    <Box display="flex" flexDirection="column" gap={1} style={{ flex: '0 0 70%' }}>
                        <TextField 
                            placeholder="Base variance" 
                            id="base-variance" 
                            size="small" 
                            fullWidth 
                            sx={{ fontSize: 11, '& .MuiInputBase-input': { fontSize: 11 } }} 
                            InputProps={{ style: { height: 25, fontSize: 11 } }}
                            value={baseVariance || ""}
                            onChange={(e) => this.setState({ baseVariance: e?.target?.value || "" })}
                        />
                        <TextField 
                            placeholder="Opposite variance" 
                            id="opposite-variance" 
                            size="small" 
                            fullWidth 
                            sx={{ fontSize: 11, '& .MuiInputBase-input': { fontSize: 11 } }} 
                            InputProps={{ style: { height: 25, fontSize: 11 } }} 
                            value={oppositeVariance || ""}
                            onChange={(e) => this.setState({ oppositeVariance: e?.target?.value || "" })}
                        />
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center" style={{ flex: '0 0 20%' }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            size="small" 
                            style={{ height: 30, fontSize: 11, width: '100%' }} 
                            disabled={!baseVariance || !oppositeVariance || semanticValueData?.length === 0 || selectedSemanticKeyId === null}
                            onClick={() => {this.getTestResult();}}
                        >
                            Run Test
                        </Button>
                    </Box>
                </Box>
            </>
        );
    }

    getSemanticKeyDropdowns = (showSearch: boolean): JSX.Element => {
        const { lobCodes, questionCodes, selectedLobCode, selectedQuestionCode } = this.state;
        return(
            <Box display="flex" gap={1} style={{ marginBottom: 10, minHeight: 0 }}>
                <Autocomplete
                    options={lobCodes}
                    getOptionLabel={(option) => option?.Lob_Name || ''}
                    value={lobCodes.find(lob => lob.Lob_Code === selectedLobCode) || null}
                    onChange={(event, value) => {
                        this.setState({ selectedLobCode: value?.Lob_Code || null }, async () => {await this.getSemanticKeyData();});
                        this.getSemanticKeyData();
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="LOB"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                style: { height: 30, fontSize: 11 }
                            }}
                            inputProps={{
                                ...params.inputProps,
                                style: { fontSize: 11 }
                            }}
                            sx={{ fontSize: 11 }}
                        />
                    )}
                    fullWidth
                    slotProps={{ listbox: { sx: { fontSize: 11 } } }}
                    sx={{ fontSize: 11 }}
                />
                <Autocomplete
                    options={questionCodes}
                    getOptionLabel={(option) => option?.ShortQuestion || ''}
                    value={questionCodes.find(q => q.ShortQuestion === selectedQuestionCode) || null}
                    onChange={(event, value) => {
                        this.setState({ selectedQuestionCode: value?.ShortQuestion || null }, async () => {await this.getSemanticKeyData();})
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Question"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                style: { height: 30, fontSize: 11 }
                            }}
                            inputProps={{
                                ...params.inputProps,
                                style: { fontSize: 11 }
                            }}
                            sx={{ fontSize: 11 }}
                        />
                    )}
                    fullWidth
                    slotProps={{ listbox: { sx: { fontSize: 11 } } }}
                    sx={{ fontSize: 11 }}
                />
                {showSearch && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        style={{ height: 30, fontSize: 11 }} 
                        disabled={!selectedLobCode || !selectedQuestionCode}
                    onClick={() => {this.getSemanticKeyData();}}
                >
                    Search
                </Button>)}
                {showSearch && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        style={{ height: 30, fontSize: 11 }} 
                        disabled={!selectedLobCode || !selectedQuestionCode}
                    onClick={() => {this.exportData();}}
                >
                    Export
                </Button>)}
            </Box>
        );
    }

    getSemanticKeySearcher = () => {
        return (
            <div>
                <TextField
                    fullWidth
                    margin="normal"
                    value={this.state.semanticKeySearch}
                    placeholder="type here to search keys..."
                    onChange={(e) => this.setState({ semanticKeySearch: e?.target?.value || "" })}
                    InputProps={{
                        style: {
                            height: 30,
                            fontSize: '12px',
                        },
                    }}
                />
            </div>
        );
    }

    addSemanticKeyValueDialog = () => {
        const { dialogState, selectedLobCode, selectedQuestionCode } = this.state;

        const saveData = async() => {
            this.setState({ loading: true });
            try{
                if (dialogState.dialogFor === 'baseVariance') {
                    await addSemanticKey(selectedLobCode!, selectedQuestionCode!, dialogState.baseVariance!);
                } else if (dialogState.dialogFor === 'oppositeVariance') {
                    await addSemanticValue(dialogState.semanticKeyId!, dialogState.oppositeVariance!);
                }
            } catch (error: any) {
                if(error?.response?.data?.detail?.includes("already exists")){
                    let errorMsg = "";
                    if(dialogState.dialogFor === 'baseVariance'){
                        errorMsg = `${dialogState?.dialogFor || ""} already exists for Question ${selectedQuestionCode} and LOB ${selectedLobCode}.`;
                    }
                    if(dialogState.dialogFor === 'oppositeVariance'){
                        errorMsg = `${dialogState?.dialogFor || ""} already exists for the selected Base Variance.`;
                    }
                    this.setState({
                        snackBar: {
                            ...this.state.snackBar,
                            open: true,
                            message: errorMsg,
                            severity: 'error'
                        }
                    });
                }
            } finally {
                this.setState({ loading: false });
                this.setDialogState(false, null, null, null, null);
                if (dialogState.dialogFor === 'baseVariance') {
                    await this.getSemanticKeyData();
                }
                else if (dialogState.dialogFor === 'oppositeVariance') {
                    await this.getSemanticValueData(dialogState.semanticKeyId!);
                }
            }
        }
        return (
            <Dialog open={dialogState.isOpen} onClose={() => this.setState({ dialogState: { ...dialogState, isOpen: false } })} fullWidth maxWidth="md">
                <DialogTitle>{dialogState.dialogFor === 'baseVariance' ? 'Add Base Variance Master' : 'Add Opposite Variance Master'}</DialogTitle>
                <DialogContent>
                    {
                        dialogState.dialogFor === 'baseVariance' && (
                            <div>
                                {this.getSemanticKeyDropdowns(false)}
                                <p>Keyword</p>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    value={dialogState.baseVariance || ""}
                                    onChange={(e) => this.setState({ dialogState: { ...dialogState, baseVariance: e?.target?.value } })}
                                    InputProps={{
                                        style: {
                                            height: 30,
                                            fontSize: '12px',
                                        },
                                    }}
                                />
                            </div>
                        )
                    }
                    {
                        dialogState.dialogFor === 'oppositeVariance' && (
                            <div>
                                <p>Keyword</p>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    value={dialogState.oppositeVariance || ""}
                                    onChange={(e) => this.setState({ dialogState: { ...dialogState, oppositeVariance: e?.target?.value } })}
                                    InputProps={{
                                        style: {
                                            height: 30,
                                            fontSize: '12px',
                                        },
                                    }}
                                />
                            </div>
                        )
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setDialogState(false, null, null, null, null)} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {await saveData()}}
                        color="primary"
                        disabled={
                            (dialogState.dialogFor === 'baseVariance' && (!dialogState?.baseVariance?.trim() || !selectedLobCode || !selectedQuestionCode) ) || 
                            (dialogState.dialogFor === 'oppositeVariance' && !dialogState?.oppositeVariance?.trim())
                        }
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
    
    deleteConfirmationDialog = () => {
        const { deleteDialogState } = this.state;

        const deleteItem = async() => {
            this.setState({ loading: true });
            try{
                if (deleteDialogState.dialogFor === 'baseVariance') {
                    await deleteSemanticKey(deleteDialogState.recordId!);
                }
                else if (deleteDialogState.dialogFor === 'oppositeVariance') {
                    await deleteSemanticValue(deleteDialogState.recordId!);
                }
            } catch (error) {
                console.error('Error deleting item:', error);
            }
            finally {
                this.setState({ loading: false });
                this.setDeleteDialogState(false, null, null);
                if (deleteDialogState.dialogFor === 'baseVariance') {
                    await this.getSemanticKeyData();
                }
                else if (deleteDialogState.dialogFor === 'oppositeVariance') {
                    await this.getSemanticValueData(this.state.selectedSemanticKeyId!);
                }
            }
        }

        return (
            <Dialog open={deleteDialogState.isOpen} onClose={() => this.setDeleteDialogState(false, null, null)} fullWidth maxWidth="sm">
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this item?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setDeleteDialogState(false, null, null)} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {await deleteItem()}}
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    render() {
        const { 
            loading, semanticKeySearch, snackBar,
            semanticKeyData, semanticValueData, selectedSemanticKeyId
        } = this.state;
        const { displaysidebar } = this.props;

        const getSemanticKeyTablerecords = (): ISemanticKeyData[] => {
            if(semanticKeySearch){
                return semanticKeyData?.filter(f => f.KeyText?.trim().toLocaleLowerCase()?.includes(semanticKeySearch?.trim()?.toLocaleLowerCase()));
            }
            return semanticKeyData;
        }
        return (
            <>
                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                    </Box>
                )}
                <GridTable displaysidebar={displaysidebar} style={{ fontSize: 11 }}>
                    <Box display="flex" flexDirection="column" height="100%" style={{ fontSize: 11 }}>
                        {this.getHeaderSection()}
                        <Box display="flex" flex={1} gap={2} style={{ minHeight: 0, fontSize: 11 }}>
                            {/* Left column - Lob Codes */}
                            <Paper elevation={3} variant="outlined" style={{ flex: 5, padding: 10, fontSize: 11, overflow: 'hidden' }}>
                                {this.getSemanticKeyDropdowns(true)}
                                {this.getSemanticKeySearcher()}
                                <div style={{ marginTop: 8 }}>
                                    {semanticKeyData?.length > 0 ? (
                                        <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '10%' }}>S.No</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '7%' }}>LobCode</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '8%' }}>QCode</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '62%' }}>KeyText</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '13%' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getSemanticKeyTablerecords()?.map((row, index) => (
                                                        <tr key={row.Id} style={{ backgroundColor: selectedSemanticKeyId != null && selectedSemanticKeyId === row.Id ? '#6264651f' : 'transparent' }}>
                                                            <td style={{ padding: 8, width: '10%' }}>{index + 1}</td>
                                                            <td style={{ padding: 8, width: '7%' }}>{row.LobCode}</td>
                                                            <td style={{ padding: 8, width: '8%' }}>{row.QuestionCode}</td>
                                                            <td style={{ padding: 8, width: '62%' }}>{row.KeyText}</td>
                                                            <td style={{ padding: 8, width: '13%' }}>
                                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                                    {/* {
                                                                        row.isActive ?
                                                                            <Cancel sx={{ cursor: "pointer" }} style={{ color: red[500], fontSize: 'large' }} />
                                                                            :
                                                                            <CheckIcon sx={{ cursor: "pointer" }} style={{ color: green[500], fontSize: 'large' }} />
                                                                    } */}
                                                                    <Tooltip title="View opposite values">
                                                                        <Visibility sx={{ cursor: "pointer", fontSize: 'large' }} 
                                                                            onClick={() => {
                                                                                this.setState({ selectedSemanticKeyId: row.Id, baseVariance: null, oppositeVariance: null });
                                                                                this.getSemanticValueData(row.Id);
                                                                            }} 
                                                                            />
                                                                    </Tooltip>
                                                                    <Delete sx={{ cursor: "pointer", fontSize: 'large' }} onClick={() => this.setDeleteDialogState(true, 'baseVariance', row.Id)} style={{ color: red[500] }}/>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <Typography variant="body2" style={{ fontSize: 12 }}>No semantic keys to display.</Typography>
                                    )}
                                </div>
                            </Paper>

                            {/* Right column - Semantic Keys / Values */}
                            <Paper elevation={3} variant="outlined" style={{ flex: 5, padding: 10, overflow: 'hidden', maxHeight: 'calc(100vh - 150px)', fontSize: 11 }}>
                                {this.getSemanticValueHeaderSection()}
                                {/* Placeholder for semantic keys rendering */}
                                <div style={{ marginTop: 8, fontSize: 11 }}>
                                    {semanticValueData?.length > 0 ? (
                                        <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '9%' }}>S.No</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '65%' }}>Value</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '10%' }}>Match Score</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '10%' }}>Base Match Score</th>
                                                        <th style={{ textAlign: 'left', padding: 8, width: '6%' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {semanticValueData.map((value, index) => (
                                                        <tr key={index}>
                                                            <td style={{ padding: 8, width: '9%' }}>{index + 1}</td>
                                                            <td style={{ padding: 8, width: '65%' }}>{value.ValueText}</td>
                                                            <td style={{ padding: 8, width: '10%' }}>{value.MatchScore}</td>
                                                            <td style={{ padding: 8, width: '10%' }}>{value.BaseMatchScore}</td>
                                                            <td style={{ padding: 8, width: '6%' }}>
                                                                <Delete sx={{ cursor: "pointer", fontSize: 'large' }} onClick={() => this.setDeleteDialogState(true, 'oppositeVariance', value.Id)} style={{ color: red[500] }} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <Typography variant="body2" style={{ fontSize: 12 }}>No semantic values found.</Typography>
                                    )}
                                </div>
                            </Paper>
                        </Box>
                    </Box>
                </GridTable>
                {this.addSemanticKeyValueDialog()}
                {this.deleteConfirmationDialog()}
                <div>
                    <SnackBarComponent open={snackBar.open} message={snackBar.message} severity={snackBar.severity} onClose={() => {
                        this.setState({
                            snackBar: {
                                ...this.state.snackBar,
                                open: false,
                                message: ''
                            }
                        });
                    }} />
                </div>
            </>
        );
    }
}